import { appIntl } from '@components/locale/IntlGlobalProvider';
import { formatDate } from '@utils/date.utils';

import { UserLight } from '@/types/authent/login.types';
import {
  EventActionEnum,
  EventLog,
  HoloLog,
  LogAction,
  LogTypeEnum,
  SystemActionEnum,
  UserActionEnum,
} from '@/types/c2/logs.type';
import { DateTimeEnum } from '@/types/dateTime.types';

const getEventLogContent = (log: EventLog): string => {
  const formatMessage = appIntl().formatMessage;

  const startDate = new Date(log.startDate);
  const params = {
    creation: log.action === EventActionEnum.EVENT_CREATION,
    name: log.name,
    observationType: formatMessage({ id: `live.event.types.${log.observationType}` }),
    date: formatDate(startDate, DateTimeEnum.DATE),
    time: formatDate(startDate, DateTimeEnum.TIME),
    duration: log.duration,
    description: log.comment.length === 0 ? 'empty' : log.comment,
  };
  return formatMessage({ id: 'components.logbook.event' }, params);
};

function getActionLabel(action: LogAction, parameters: string[]): string {
  const params = { ...parameters } as { [p: number]: string };
  return appIntl().formatMessage({ id: `components.logbook.actions.${action}` }, params);
}

export function getLogContent(log: HoloLog): string {
  switch (log.type) {
    case LogTypeEnum.EVENT:
      return getEventLogContent(log);
    case LogTypeEnum.INSTRUCTION:
      return log.instruction;
    case LogTypeEnum.SYSTEM: {
      if (log.action === SystemActionEnum.NEW_ALERT) {
        return getActionLabel(SystemActionEnum.NEW_ALERT, [
          ...log.parameters.slice(0, 3),
          appIntl().formatMessage({ id: `components.logbook.new_alert.${log.parameters[3]}` }),
          ...log.parameters.slice(4),
        ]);
      } else {
        return getActionLabel(log.action, log.parameters);
      }
    }
    case LogTypeEnum.USER_ACTION:
      if (log.action === UserActionEnum.NEUTRAL) {
        return getActionLabel(UserActionEnum.NEUTRAL, [
          log.parameters[0],
          log.parameters.slice(1, log.parameters.length).join(', '),
        ]);
      } else {
        return getActionLabel(log.action, log.parameters);
      }
    default:
      return '';
  }
}

export function getLogAuthor(log: HoloLog): UserLight | null {
  const defaultAuthor = (nameKey: string): UserLight => ({
    id: 0,
    name: appIntl().formatMessage({ id: nameKey }),
    firstName: '',
    login: '',
    email: '',
  });

  switch (log.type) {
    case LogTypeEnum.EVENT:
    case LogTypeEnum.INSTRUCTION:
    case LogTypeEnum.USER_ACTION:
      return log.author ?? defaultAuthor('authent.user.unknownUser');
    case LogTypeEnum.SYSTEM:
      return defaultAuthor('authent.user.system');
    default:
      return null;
  }
}
